/** @see https://open.pinduoduo.com/application/document/browse?idStr=C628565D8883F05F */
import axios, { AxiosRequestConfig } from "axios";
import OrderDTO from "@/dto/order/OrderDTO";
import OrderMergeSplitStatus from "@/enum/OrderMergeSplitStatus";
import AuthTokenHolder, { TOKEN_KEY } from "./http/AuthTokenHolder";
import { Modal } from "antd";
import OrderConsigneeDTO from "@/dto/order/OrderConsigneeDTO";
import Cookie from "js-cookie";
import tbPCAxiosAdapter from "@qn/services/http/tbAxiosAdapter";
import { PDD_OMS_BASE_URL } from "@/config";
import pddDeviceFingerPrintInterceptor from "./http/PddDeviceFingerPrint/PddDeviceFingerPrintInterceptor";
import { CallbackUrl } from "./callback";
import { getRealOrderOriginalId, hasLocalConsigneeInfo } from "@/utils/OrderUtils";

const adapter = import.meta.env.VITE_IS_QN ? tbPCAxiosAdapter : null;

export const PDD_VERIFY_AUTH_TOKEN = "pddVerifyAuthToken_";

// 拼多多
const pddAxios = axios.create({
    baseURL: PDD_OMS_BASE_URL,
    timeout: 30000,
    withCredentials: true,
    adapter,
});

pddAxios.interceptors.request.use(pddDeviceFingerPrintInterceptor);

/**用于商家报备解密场景。 */
enum DecryptReportType {
    /**默认的方式，解密获取虚拟号, 手机号是虚拟号，地址末尾加4位分机号 */
    DEFAULT = 0,

    /**使用非拼电子面单发货，解密获取原始明文，手机号是明文，地址是原始明文 */
    NOT_PDD_EWAYBILL,

    /**需要第三方代发，解密获取原始明文，手机号是明文，地址是原始明文 */
    THIRD_SHIP,

    /**需要线下手动发货，解密获取原始明文，手机号是明文，地址是原始明文 */
    MANUAL_SHIP,
}

interface OrderDecryptParams {
    /**订单所属的店铺 */
    shopId: number;

    decrypt_report_type?: DecryptReportType;

    /**
     * 拼多多订单编号，如果是合并订单则用子订单订单编号
     * 如果是拆分订单需要从拆分后的订单号中解析出父订单的订单号，获取最后一个-号，该-号前面的字符串是父订单订单号
     */
    order_sn: string;

    page_table_id?: string;

    request_id: string;
}

export interface PddOrderInfo {
    receiver_address: string;
    receiver_name: string;
    receiver_phone: string;

    // 注：

    // 如果是针对 receiver_phone 的解密，返回结果中会额外有两个字段：

    // virtual_number_type：虚拟号类型：0-非虚拟号 1-虚拟号

    // virtual_identify_number：虚拟号分机号，若虚拟号类型为 1，则返回该字段；若为 0，则不返回

    virtual_number_type?: 0 | 1;

    virtual_identify_number?: string;

    // 对应发货易第四级地址
    street: string;

    local_receiver_province: string;
    local_receiver_city: string;
    local_receiver_district: string;
    local_receiver_street: string;
    local_receiver_mobile: string;
    local_receiver_name: string;
}

interface OrderDecryptSuccessResult {
    client_id: string;
    mall_id: number;
    decrypt_report_type: number;
    error_code: 0;
    error_msg: string;

    order_info: PddOrderInfo;

    order_sn: string;
    page_table_id?: string;
    request_id: string;
    sub_code: string;
    sub_msg: string;
}

interface OrderDecryptRiskResult {
    error_code: 54001;

    client_id: string;

    mall_id: number;

    risk_info: {
        verify_auth_token: string;
    };
}

type OrderDecryptResult = OrderDecryptSuccessResult | OrderDecryptRiskResult;

function pddOrderDecrypt(params: OrderDecryptParams, config?: AxiosRequestConfig) {
    return pddAxios.post<OrderDecryptResult>("/pdd/control/decrypt/v1/address", params, config);
}

function getPddVerityUrl(shopId: number, mallId: number, clientId: string, verifyAuthToken: string) {
    const redirectUrlSearch = new URLSearchParams();
    const toUrl = location.pathname + location.search;
    redirectUrlSearch.set("shopId", shopId.toString());
    redirectUrlSearch.set("to", encodeURIComponent(toUrl));

    const search = new URLSearchParams();
    const redirectUrl = `${location.origin}${CallbackUrl.PDD_VERIFY_AUTH}?${redirectUrlSearch}`;

    search.set("mall_id", mallId.toString());
    search.set("client_id", clientId);
    search.set("verifyAuthToken", verifyAuthToken);
    search.set("redirect_url", redirectUrl);

    return `https://fuwu.pinduoduo.com/service-market/are-you-robot?${search.toString()}`;
}

export interface PddOrderDecryptResult {
    successful: boolean;
    message: string | JSX.Element;
    isNeedVerify?: boolean;
    isOriginalAddressDecryptForbidden?: boolean;
    url?: string;
    data?: {
        id: number;
        consignee: OrderConsigneeDTO;
        // 是否为隐私号
        privacyProtection?: boolean;
        // 区号
        virtualIdentifyNo?: string;
    };
}

/**
 * 拼多多订单解密
 */
export default class PddOrderDecryptor {
    constructor(order: OrderDTO, decryptReportType?: DecryptReportType) {
        this.order = order;

        this.decryptReportType = decryptReportType || DecryptReportType.DEFAULT;
    }

    order: OrderDTO;

    decryptReportType: DecryptReportType;

    static pddVerityConfirm(url: string) {
        Modal.confirm({
            title: "是否拼多多安全授权？",
            content: "获取信息失败，由于频繁查看收货人完整信息被拼多多安全机制拦截，请点击下方按钮进行安全验证！",
            okText: "请点击这里进行安全验证",
            okButtonProps: {
                danger: true,
                type: "primary",
                href: url,
            },
        });
    }

    async decrypt(): Promise<PddOrderDecryptResult> {
        const shopId = this.order.shopId;
        const isMergerOrder = this.order.mergeSplitStatus === OrderMergeSplitStatus.MERGE_PARENT;
        const isSplitChildOrder = this.order.mergeSplitStatus === OrderMergeSplitStatus.SPLIT_CHILD;

        if (isMergerOrder && this.order?.childOrders?.length) {
            const result = await this._doDecrypt(shopId, getRealOrderOriginalId(this.order));

            return result;
        }

        if (isSplitChildOrder) {
            const orderOriginalId = this.order.originalId.replace(/-[\d]{1,}$/, "");

            return this._doDecrypt(shopId, orderOriginalId);
        }

        return this._doDecrypt(shopId, this.order.originalId);
    }

    async _doDecrypt(shopId: number, order_sn: string): Promise<PddOrderDecryptResult> {
        const pddVerifyAuthToken = Cookie.get(`${PDD_VERIFY_AUTH_TOKEN}${shopId}`);
        const axiosRequestConfig: AxiosRequestConfig = {
            headers: {
                [TOKEN_KEY]: AuthTokenHolder.getToken(),
                // 强制启动拼多多安全验证
                // "X-PDD-MustVerify": "True",
            },
        };

        if (pddVerifyAuthToken) {
            axiosRequestConfig.headers["X-PDD-VerifyAuthToken"] = pddVerifyAuthToken;
        }

        const response = await pddOrderDecrypt(
            {
                request_id: `${new Date().getTime()}_${order_sn}`,
                decrypt_report_type: this.decryptReportType,
                order_sn: order_sn,
                shopId,
            },
            axiosRequestConfig,
        );

        if (!response.data) {
            return {
                successful: false,
                message: "获取信息失败，请稍候重试！",
            };
        }

        if (response.data.error_code === 54001) {
            const mallId = response.data.mall_id;
            const clientId = response.data.client_id;
            const verifyAuthToken = response.data.risk_info.verify_auth_token;
            const pddVerityUrl = getPddVerityUrl(shopId, mallId, clientId, verifyAuthToken);

            return {
                successful: false,
                isNeedVerify: true,
                url: pddVerityUrl,
                message: "请进行拼多多安全验证",
            };
        } else {
            const errorMsg = response.data.error_msg;
            const subErrorMsg = response.data.sub_msg;

            if (errorMsg && errorMsg.indexOf("加密数据tag错误") > -1) {
                return {
                    successful: false,
                    message: "加密数据tag错误",
                };
            } else if (errorMsg || subErrorMsg) {
                if (errorMsg && errorMsg === "当前店铺解密额度为0") {
                    return {
                        successful: false,
                        message: (
                            <div>
                                当前店铺解密额度为0，请申请解密额度，
                                <a href="https://qw15p3s8hv.feishu.cn/wiki/wikcnOlev5vWsWpr7QkVq6hdo9f" target="_blank">
                                    点击这里查看申请教程
                                </a>
                            </div>
                        ),
                    };
                } else if (errorMsg && errorMsg === "数据解密太频繁") {
                    return {
                        successful: false,
                        message: "获取信息失败，数据解密太频繁，请稍候重试！",
                    };
                } else if ((response.data.error_code as any) === 11001 && errorMsg && errorMsg.includes("禁止解密")) {
                    const orderInfo = response.data.order_info;
                    const hasOrderLocalInfo = hasLocalConsigneeInfo(this.order.consignee);
                    const hasLocalDecryptInfo =
                        orderInfo &&
                        (orderInfo.local_receiver_province ||
                            orderInfo.local_receiver_city ||
                            orderInfo.local_receiver_district ||
                            orderInfo.local_receiver_mobile ||
                            orderInfo.local_receiver_street ||
                            orderInfo.local_receiver_name);

                    if (hasOrderLocalInfo && hasLocalDecryptInfo) {
                        const consignee = { ...this.order.consignee };

                        if (orderInfo.local_receiver_province) {
                            consignee.localProvince = orderInfo.local_receiver_province;
                        }

                        if (orderInfo.local_receiver_city) {
                            consignee.localCity = orderInfo.local_receiver_city;
                        }

                        if (orderInfo.local_receiver_district) {
                            consignee.localDistrict = orderInfo.local_receiver_district;
                        }

                        if (orderInfo.local_receiver_street) {
                            consignee.localStreet = orderInfo.local_receiver_street;
                        }

                        if (orderInfo.local_receiver_mobile) {
                            consignee.localMobile = orderInfo.local_receiver_mobile;
                        }

                        if (orderInfo.local_receiver_name) {
                            consignee.localName = orderInfo.local_receiver_name;
                        }

                        return {
                            successful: false,
                            isOriginalAddressDecryptForbidden: true,
                            data: {
                                id: this.order.id,
                                consignee,
                                privacyProtection: this.order.consignee.privacyProtection,
                                virtualIdentifyNo: this.order.consignee.virtualIdentifyNo,
                            },
                            message: "拼多多平台接口禁止解密原地址，如需查看原地址请到拼多多商家后台查看",
                        };
                    }

                    return {
                        successful: false,
                        message: "拼多多平台接口禁止解密，请到拼多多商家后台查看",
                    };
                } else {
                    return {
                        successful: false,
                        message: `获取信息失败，${errorMsg || ":"}${subErrorMsg || ""}，请重试或联系客服反馈！`,
                    };
                }
            } else {
                const {
                    receiver_address,
                    receiver_name,
                    receiver_phone,
                    virtual_identify_number,
                    virtual_number_type,
                    street,
                    local_receiver_province,
                    local_receiver_city,
                    local_receiver_district,
                    local_receiver_street,
                    local_receiver_mobile,
                    local_receiver_name,
                } = response.data.order_info;

                const consignee = {
                    ...this.order.consignee,
                    name: receiver_name,
                    street: receiver_address,
                    town: street,
                    mobile: receiver_phone,
                };

                if (local_receiver_province) {
                    consignee.localProvince = local_receiver_province;
                }

                if (local_receiver_city) {
                    consignee.localCity = local_receiver_city;
                }

                if (local_receiver_district) {
                    consignee.localDistrict = local_receiver_district;
                }

                if (local_receiver_street) {
                    consignee.localStreet = local_receiver_street;
                }

                if (local_receiver_mobile) {
                    consignee.localMobile = local_receiver_mobile;
                }

                if (local_receiver_name) {
                    consignee.localName = local_receiver_name;
                }

                return {
                    successful: true,
                    data: {
                        id: this.order.id,
                        consignee,
                        privacyProtection: virtual_number_type === 1,
                        virtualIdentifyNo: virtual_identify_number,
                    },
                    message: "获取成功",
                };
            }
        }
    }
}
